import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react';
import {
  PlayButton,
  TextTruncate,
  VideoLightbox,
  WrappedImage,
} from '@time-line/components/common';
import { useVttTracks } from '@time-line/components/hooks';
import { VerifiedTick } from '@time-line/components/icons';
import type { Testimonial as TestimonialType } from 'types/cms/blocks/TestimonialGroup';

interface TestimonialProps {
  testimonial: TestimonialType;
  className: string;
}

export function Testimonial({
  testimonial: {
    quote: { text, attribution },
    video,
  },
  className,
}: TestimonialProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const tracks = useVttTracks(video?.vtt);
  const posterImage = attribution.image || video?.poster;

  return (
    <Box className={className}>
      {video?.cloudinaryId && (
        <>
          <Box
            as="button"
            type="button"
            onClick={onOpen}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={`Play video: ${attribution.name}`}
            position="relative"
            display="block"
            w="full"
            borderRadius="base"
            overflow="hidden"
            cursor="pointer"
          >
            {posterImage && (
              <WrappedImage
                image={posterImage}
                ratio={4 / 5}
                sizes="(min-width: 768px) 25vw, (min-width: 1920px) 345px, 100vw"
              />
            )}
            <PlayButton
              aria-hidden
              as="span"
              size="xl"
              position="absolute"
              bottom="lg"
              left="50%"
              transform="translateX(-50%)"
            />
          </Box>
          <VideoLightbox
            isOpen={isOpen}
            onClose={onClose}
            cloudinaryId={video.cloudinaryId}
            title={attribution.name}
            videoTitle={video.title}
            poster={video.poster || attribution.image}
            tracks={tracks}
          />
        </>
      )}
      {!video?.cloudinaryId && attribution.image && (
        <WrappedImage
          image={attribution.image}
          ratio={4 / 5}
          sizes="(min-width: 768px) 25vw, (min-width: 1920px) 345px, 100vw"
        />
      )}
      <Box mt="sm">
        <Flex align="center" gap="5xs">
          <Text as="h2" size="title">
            {attribution.name}
          </Text>
          {attribution.isVerified && (
            <VerifiedTick color="rgba(66, 165, 245, 1)" width="16px" />
          )}
        </Flex>
        <Text size="overline" color="blackAlpha.700" mt="4xs" mb="2xs">
          {attribution.role}
        </Text>
        <TextTruncate noOfLines={5} expandText="Read more" size="body-serif">
          “{text}”
        </TextTruncate>
      </Box>
    </Box>
  );
}
